import { UserClaimTypes, UserManager } from "app/core";
import React from "react";
import {
  AlertMessage,
  Button,
  DropdownInput,
  IGroupValidationResult,
  Input,
  InputGroup,
  InputSizes,
  IOption,
  IPopupProps,
  ListOpenDirection,
  NotificationToast,
  Popup,
  required,
  Spinner,
  WatchedFunction,
} from "component-library";

import { PropType } from "shared/utils/types";

import {
  IAgencyList,
  INewShortlist,
  IShortlist,
  IShortlistPerformer,
  IShortlists,
} from "models";

import { IActionsFooterRowContent } from "../../ActionsFooterRow";

import "./ShortlistSection.scss";

const POPUP_SIZE = { lg: 4, md: 6 };
const TOAST_LIFE_TIME = 6000;
const MAX_NAME_LENGTH = 249;
const NEW_SHORT_LIST_OPTION = { value: -1 };
const SELECTED_SHORTLIST_ID_KEY = "selectedShortlistId";
const VALIDATE = new WatchedFunction(() => undefined);

const GET_VALIDATION_DEFAULT = () => ({
  invalid: false,
  validationMessage: "",
});

interface IShortListForm {
  isNewShortlist: boolean;
  shortlistId?: number;
  shortlistName?: string;
}

declare type ShortListFormValidation = {
  [T in keyof Required<Pick<IShortListForm, "shortlistName">>]: {
    invalid: boolean;
    validationMessage: string;
  };
};

interface IShortlistSectionState {
  isToastVisible: boolean;
  linkToShortList: string;
  model: IShortListForm;
  showSpinner: boolean;
  isShortlistPopupVisible: boolean;
  isFormSubmitted: boolean;
  isShortlistsInitialized: boolean;
  validationData: ShortListFormValidation;
  isValid: boolean;
}

export interface IShortlistSectionProps {
  content: IActionsFooterRowContent;
  shortlists?: IShortlists;
  agencies?: IAgencyList;
  addShortList: (model: INewShortlist) => Promise<string>;
  addPerformerShortList: (
    shortlistId: number,
    model: IShortlistPerformer
  ) => Promise<string>;
}

export class ShortlistSection extends React.Component<
  IShortlistSectionProps,
  IShortlistSectionState
> {
  public readonly state: Readonly<IShortlistSectionState> = {
    isToastVisible: false,
    isValid: false,
    linkToShortList: "",
    model: {
      isNewShortlist: false,
      shortlistName: "",
      shortlistId: NEW_SHORT_LIST_OPTION.value,
    },
    isShortlistPopupVisible: false,
    showSpinner: false,
    isFormSubmitted: false,
    isShortlistsInitialized: false,
    validationData: {
      shortlistName: GET_VALIDATION_DEFAULT(),
    },
  };

  componentDidUpdate(
    prevProps: Readonly<IShortlistSectionProps>,
    prevState: Readonly<IShortlistSectionState>
  ) {
    if (prevProps.shortlists !== this.props.shortlists) {
      const {
        model: { shortlistId },
        isShortlistsInitialized,
      } = this.state;
      const activeList = this.getActiveList();
      const isShortlistInitializedState =
        isShortlistsInitialized ||
        (!isShortlistsInitialized && activeList.length > 0);
      let selectedShortListId = shortlistId;

      if (!isShortlistsInitialized && activeList.length > 0) {
        const preselectedShortlistId = parseInt(
          localStorage.getItem(SELECTED_SHORTLIST_ID_KEY) || "",
          10
        );

        selectedShortListId =
          preselectedShortlistId === NEW_SHORT_LIST_OPTION.value
            ? NEW_SHORT_LIST_OPTION.value
            : activeList.find((item) => item.id === preselectedShortlistId)
            ? preselectedShortlistId
            : activeList[0].id;
      }

      if (shortlistId !== selectedShortListId) {
        this.setState({
          isShortlistsInitialized: isShortlistInitializedState,
          model: { ...this.state.model, shortlistId: selectedShortListId },
        });
      }
    }
  }

  public render() {
    const { content } = this.props;
    const { showSpinner, isShortlistPopupVisible } = this.state;

    return (
      <div className="c-shortlist-section__container g-col-lg-7 g-col-md-7 g-col-sm-12 g-inner-grid">
        {this.renderToast()}
        <div className="c-shortlist-section__dropdown g-col-lg-4 g-col-md-4 g-col-sm-12">
          {this.renderShortlistDropdown()}
        </div>
        <div className="c-shortlist-section__button g-col-lg-3 g-col-md-3 g-col-sm-12">
          <Button
            type="primary"
            size="medium"
            loading={showSpinner && !isShortlistPopupVisible}
            text={content.addToShortlistLabel}
            onClick={this.setShortList}
          />
        </div>
        {isShortlistPopupVisible && this.renderPopup()}
      </div>
    );
  }

  private renderToast = () => {
    const { content } = this.props;
    const {
      linkToShortList,
      isToastVisible,
      model: { shortlistName },
    } = this.state;

    const link = (
      <span className="c-shortlist-section__toast-text">
        {content.performerAddedToast + " "}
        <a
          className="c-shortlist-section__toast-link g-alternative-link g-alternative-link--lg"
          target="_blank"
          href={linkToShortList}
          tabIndex={0}
        >
          {shortlistName}
        </a>
      </span>
    );

    return (
      <NotificationToast
        stickToComponent
        show={isToastVisible}
        handleHide={this.hideToast}
        text={link}
        timeoutDuration={TOAST_LIFE_TIME}
      />
    );
  };

  private renderSpinner = () => (
    <div className="c-shortlist-section__spinner">
      <Spinner />
    </div>
  );

  private renderPopupInput = () => {
    const { content } = this.props;
    const {
      isFormSubmitted,
      model: { shortlistName },
      validationData,
    } = this.state;
    const formConfig = this.getFormConfiguration();

    return (
      <InputGroup
        className="c-shortlist-section__form"
        validationDone={this.validationPerformed}
        triggerValidation={VALIDATE}
        inputConfigs={formConfig}
      >
        <Input
          id="shortlistFormInput"
          label={content.newShortlistLabel}
          value={shortlistName}
          valueChanged={this.setCustomShortListName}
          type="text"
          isFormSubmitted={isFormSubmitted}
          maxLength={MAX_NAME_LENGTH}
          invalid={validationData.shortlistName.invalid}
          validationMessage={validationData.shortlistName.validationMessage}
        />
      </InputGroup>
    );
  };

  private renderShortlistDropdown = () => {
    const { content } = this.props;
    const {
      model: { shortlistId },
      isFormSubmitted,
    } = this.state;
    const activeList = this.getActiveList();

    const shortlistOptions = this.getShortlistOptions(
      content.newShortlistOption,
      activeList
    );
    const selectedOption = shortlistOptions.find(
      (option) => option.value === shortlistId
    );

    return (
      <DropdownInput<number>
        id="shortlistsSectionShortlist"
        variant="select"
        placeholder={content.shortlistPlaceholder}
        options={shortlistOptions}
        optionSelected={this.selectShortList}
        isFormSubmitted={isFormSubmitted}
        size={InputSizes.Medium}
        listOpenDirection={ListOpenDirection.Up}
        truncateOptions
        selected={selectedOption}
        searchInput={{
          show: true,
          placeholder: content.shortlistSearchPlaceholder,
        }}
      />
    );
  };

  private renderPopup = () => {
    const { showSpinner } = this.state;

    let component;

    if (showSpinner) {
      component = this.renderSpinner();
    } else {
      component = this.renderPopupInput();
    }

    return this.wrapContent(component);
  };

  private wrapContent = (children: React.ReactNode) => {
    const { shortlists, content } = this.props;
    const popupSetting = {
      width: POPUP_SIZE,
      close: this.closeShortlistPopup,
      texts: { closePopup: "" },
      priority: "medium" as PropType<IPopupProps, "priority">,
    };

    const alertMessageSetting = {
      texts: {
        title: content.addToShortlistHeader,
      },
      buttons: new Array(),
    };

    if (shortlists) {
      alertMessageSetting.buttons.push({
        name: content.saveShortlist,
        click: this.createShortlist,
        type: "primary",
      });
    }

    alertMessageSetting.buttons.push({
      name: content.cancelShortlist,
      click: this.closeShortlistPopup,
      type: "secondary",
    });

    return (
      <Popup {...popupSetting}>
        <AlertMessage {...alertMessageSetting}>{children}</AlertMessage>
      </Popup>
    );
  };

  private setShortList = async () => {
    const { model, showSpinner } = this.state;
    const { addPerformerShortList, agencies, shortlists } = this.props;

    if (Number(model.shortlistId) > NEW_SHORT_LIST_OPTION.value) {
      if (agencies && shortlists && !showSpinner) {
        this.setState({ isFormSubmitted: true, showSpinner: true });

        const shortlistPerformer = this.getPerformerShortListModel(agencies);

        let shortlistName = "";

        const { name = "" } =
          shortlists.shortlists.find(
            (shortlist) => shortlist.id === model.shortlistId
          ) || {};

        shortlistName = name;

        const linkToShortList = await addPerformerShortList(
          model.shortlistId || 0,
          shortlistPerformer
        );
        this.setState({
          showSpinner: false,
          isFormSubmitted: false,
          isToastVisible: true,
          linkToShortList,
          model: { ...model, shortlistName },
        });
      }
    } else {
      this.setState(
        { model: { ...model, isNewShortlist: true }, isFormSubmitted: false },
        this.openShortlistPopup
      );
    }
  };

  private hideToast = () => this.setState({ isToastVisible: false });

  private getActiveList = () => {
    const { shortlists, agencies } = this.props;

    return shortlists && agencies ? shortlists!.shortlists : [];
  };

  private getShortlistOptions = (
    newShortlistLabel: string,
    shortList: IShortlist[]
  ) => {
    const array = shortList.map(({ id: value, name }) => ({ value, name }));
    array.unshift({ ...NEW_SHORT_LIST_OPTION, name: newShortlistLabel });

    return array;
  };

  private selectShortList = (selectedOption?: IOption<number>) => {
    this.setState(
      {
        model: {
          ...this.state.model,
          shortlistId: selectedOption?.value,
          isNewShortlist: NEW_SHORT_LIST_OPTION.value === selectedOption?.value,
        },
      },
      () => {
        if (this.state.model.isNewShortlist) {
          this.openShortlistPopup();
        }
      }
    );
  };

  private getFormConfiguration = () => {
    const { content } = this.props;
    const {
      model: { shortlistName },
    } = this.state;
    return [
      {
        name: "shortlistName",
        value: shortlistName,
        validators: [required(content.shortlistNameRequired)],
      },
    ];
  };

  private validationPerformed = (result: IGroupValidationResult) => {
    const validationData = {
      ...this.state.validationData,
      ...result.validationData,
    };
    this.setState({ validationData, isValid: !result.isGroupInvalid });
  };

  private setCustomShortListName = (newName: string) =>
    this.setState(
      (state) => ({ model: { ...state.model, shortlistName: newName } }),
      VALIDATE.call
    );

  private getPerformerShortListModel = (
    agencies: IAgencyList
  ): IShortlistPerformer => {
    const [agency] = [...agencies.agents].sort(
      (left, right) => (left.position || 0) - (right.position || 0)
    );
    const [school] = [...agencies.schools].sort(
      (left, right) => (left.position || 0) - (right.position || 0)
    );

    const agencyId = agency ? agency.refNumber : school.refNumber;

    return {
      artistRef: UserManager.artistRef || "",
      publicationCode: +(UserManager.getClaim(UserClaimTypes.artistType) || ""),
      agent: agencyId,
      comment: "",
    };
  };

  private openShortlistPopup = () =>
    this.setState({
      isShortlistPopupVisible: true,
      model: { ...this.state.model, shortlistName: "" },
    });

  private closeShortlistPopup = () =>
    this.setState({ isShortlistPopupVisible: false });

  private createShortlist = async () => {
    const { model, isValid, showSpinner } = this.state;
    const { addShortList, agencies } = this.props;

    this.setState({ isFormSubmitted: true });

    if (model.isNewShortlist && isValid && agencies && !showSpinner) {
      this.setState({ showSpinner: true });

      const shortlistPerformer = this.getPerformerShortListModel(agencies);

      const shortlist = {
        name: model.shortlistName || "",
        performers: [shortlistPerformer],
      };

      const linkToShortList = await addShortList(shortlist);

      this.setState({
        showSpinner: false,
        isToastVisible: true,
        isFormSubmitted: false,
        isShortlistsInitialized: false,
        isShortlistPopupVisible: false,
        linkToShortList,
        model: { ...model },
      });
    }
  };
}
